import { formatLabel } from '../../utils/formatLabel'
import ensureLabelLength from '../../utils/ensureLabelLength'

export function getChartData(results, isMatrix, isPercent, colors, datasets) {
  const labels = results.map(result =>
    formatLabel(isMatrix ? result.label : result.label),
  )

  const data = results.map(result =>
    isPercent ? Number(result.percentage.replace('%', '')) : result.count,
  )

  return {
    labels: labels,

    datasets: [
      {
        label: '',
        backgroundColor: colors,
        data,
        ...datasets,
      },
    ],
  }
}

export function getChartOptions(isPercent, optionsSettings) {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: optionsSettings?.indexAxis || '',

    scales: optionsSettings?.scales,

    plugins: {
      ...optionsSettings?.plugins,
    },
  }

  if (isPercent) {
    chartOptions.scales = optionsSettings?.scalesPercent

    chartOptions.plugins.tooltip = {
      callbacks: {
        label: tooltipItem => `${tooltipItem.raw}%`,
      },
    }
  }

  return chartOptions
}

export function exportChart(
  Pptxgen,
  results,
  isPercent,
  question_result,
  isMatrix,
  colors,
  chartIndex,
  exportSettings,
) {
  if (isMatrix) {
    exportChartMatrix(Pptxgen, isPercent, question_result, colors, exportSettings)

    return
  }

  const labels = results.map(result => ensureLabelLength(result.label))

  const data = results.map(result =>
    isPercent ? Number(result.percentage.replace('%', '')) : result.count,
  )

  const pres = new Pptxgen()

  const slide = pres.addSlide()

  slide.addText(question_result.title, {
    x: 0.2,
    y: 0.5,
    color: '696969',
    fontSize: 16,
  })

  const options = {
    x: 0.8,
    y: 1.4,
    w: 6.0,
    h: 3.0,
    chartColors: colors.map(color => color.replace('#', '')),
    ...exportSettings.optionsSettings,
  }

  if (isPercent) {
    options.valAxisMinVal = 0
    options.valAxisMaxVal = 100
  }

  slide.addChart(
    pres.charts[exportSettings.typeCharts],
    [
      {
        name: '',
        labels: labels,
        values: data,
      },
    ],

    options,
  )

  pres.writeFile({ fileName: 'chart' })
}

export function exportChartMatrix(
  Pptxgen,
  isPercent,
  question_result,
  colors,
  exportSettings,
) {
  const pres = new Pptxgen()

  question_result.results.forEach(result => {
    const labels = result.subAnswers.map(subAnswer => ensureLabelLength(subAnswer.label))

    const data = result.subAnswers.map(subAnswer =>
      isPercent ? Number(subAnswer.percentage.replace('%', '')) : subAnswer.count,
    )

    const slide = pres.addSlide()

    slide.addText(question_result.title, {
      x: 0.2,
      y: 0.5,
      color: '696969',
      fontSize: 16,
    })

    const question = result.label

    slide.addText(question, {
      x: 0.5,
      y: 1,
      color: '6b6b6b',
      fontSize: 14,
    })

    const options = {
      x: 0.8,
      y: 1.4,
      w: 6.0,
      h: 3.0,
      chartColors: colors.map(color => color.replace('#', '')),
      ...exportSettings.optionsSettings,
    }

    if (isPercent) {
      options.valAxisMinVal = 0
      options.valAxisMaxVal = 100
    }

    slide.addChart(
      pres.charts[exportSettings.typeCharts],
      [
        {
          name: '',
          labels: labels,
          values: data,
        },
      ],

      options,
    )
  })

  pres.writeFile({ fileName: 'chart' })
}

export function getStackedBarChartData(
  question_result,
  results,
  isMatrix,
  isPercent,
  colors,
) {
  const hasOneColor = question_result?.chartSettings?.theme?.hasOneColor

  const labels = results.map(result => ensureLabelLength(result.label))

  const labelDatasets = isMatrix
    ? question_result?.results.map(result => ensureLabelLength(result.label))
    : ['']

  const datasets = results.map((_, index) => ({
    label: labels[index],

    data: isMatrix
      ? question_result?.results.map(result => {
          return isPercent
            ? Number(result.subAnswers[index].percentage.replace('%', ''))
            : result.subAnswers[index].count
        })
      : [
          isPercent
            ? Number(results[index].percentage.replace('%', ''))
            : results[index].count,
        ],

    backgroundColor: hasOneColor ? colors[0] : colors[index],
  }))

  return {
    labels: labelDatasets,
    datasets: datasets,
  }
}

export function getStackedBarChartOptions(results, isPercent) {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',

    plugins: {
      legend: {
        align: 'start',

        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 10,
        },
      },
    },

    scales: {
      x: {
        stacked: true,
        min: 0,
        max: results.reduce((acc, result) => acc + result.count, 0),
      },

      y: {
        stacked: true,
      },
    },
  }

  if (isPercent) {
    chartOptions.scales.x = {
      stacked: true,
      min: 0,
      max: 100,

      ticks: {
        callback: value => `${value}%`,
      },

      scaleLabel: {
        display: true,
      },
    }

    chartOptions.plugins.tooltip = {
      callbacks: {
        label: tooltipItem => `${tooltipItem.dataset.label}: ${tooltipItem.raw}%`,
      },
    }
  }

  return chartOptions
}

export function getStackedBarExportChart(
  Pptxgen,
  results,
  isPercent,
  question_result,
  isMatrix,
  colors,
) {
  const hasOneColor = question_result?.chartSettings?.theme?.hasOneColor

  const labels = results.map(result => ensureLabelLength(result.label))

  const labelDatasets = isMatrix
    ? question_result?.results.map(result => ensureLabelLength(result.label))
    : ['']

  const pres = new Pptxgen()

  const slide = pres.addSlide()

  slide.addText(question_result.title, {
    x: 0.2,
    y: 0.5,
    color: '696969',
    fontSize: 16,
  })

  const options = {
    x: 0.8,
    y: 1.4,
    w: 6.0,
    h: 3.0,
    barDir: 'bar',
    barGrouping: 'stacked',
    chartColors: colors,
    showLegend: true,
    valAxisMinVal: 0,
    valAxisMaxVal: isPercent ? 100 : question_result.count,
  }

  const datasets = results.map((_, index) => ({
    name: labels[index],
    labels: labelDatasets,
    values: isMatrix
      ? question_result?.results.map(result => {
          return isPercent
            ? Number(result.subAnswers[index].percentage.replace('%', ''))
            : result.subAnswers[index].count
        })
      : [
          isPercent
            ? Number(results[index].percentage.replace('%', ''))
            : results[index].count,
        ],

    backgroundColor: hasOneColor ? colors[0] : colors[index],
  }))

  slide.addChart(pres.charts.BAR, datasets, options)

  pres.writeFile({ fileName: 'chart' })
}
